import { Box, Typography, useMediaQuery } from '@mui/material';
import { ChangeEvent, FC, ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { AgreementBox } from 'src/features/agreements/agreement-box';
import { AgreementCheckbox } from 'src/features/agreements/agreement-checkbox';
import { EmailForm } from 'src/features/agreements/email-form';
import { theme } from 'src/styles/theme';

import { EmailValidationState } from './enum/EmailValidationState';
import { styles } from './styles';

export type AgreementProps = {
    checkboxVal: boolean;
    emailAddress: string;
    handleEmailChange: (event: ChangeEvent<HTMLInputElement>) => void;
    handleCheckboxChange: (event: ChangeEvent<HTMLInputElement>) => void;
    emailValidationState: EmailValidationState;
    title: string;
    subtitle: string;
    content: ReactNode;
    checkboxContent: string;
    emailContent: string;
    submitButton: ReactNode;
};

export const Agreement: FC<AgreementProps> = ({
    checkboxVal,
    handleCheckboxChange,
    emailAddress,
    handleEmailChange,
    emailValidationState,
    title,
    subtitle,
    content,
    checkboxContent,
    emailContent,
    submitButton,
}) => {
    const { t } = useTranslation();
    const isSmallScreen = useMediaQuery(theme.breakpoints.down('lg'));

    const translatedContent = typeof content === 'string' ? t(content) : (content as string);

    return (
        <Box css={styles.wrapper}>
            <Box css={isSmallScreen && styles.padded}>
                <Typography variant="h3" css={styles.title}>
                    {t(title)}
                </Typography>

                {isSmallScreen && <Typography variant="subtitle3">{t(subtitle)}</Typography>}

                <AgreementBox isSmallScreen={isSmallScreen} content={translatedContent} />
            </Box>

            <Box css={isSmallScreen && styles.signTickEmailWrapper}>
                <Box css={isSmallScreen && styles.padded}>
                    <AgreementCheckbox
                        content={t(checkboxContent)}
                        value={checkboxVal}
                        onChange={handleCheckboxChange}
                    />
                    <Typography variant="body1">{t(emailContent)}</Typography>
                    <EmailForm value={emailAddress} onChange={handleEmailChange} />
                    {emailValidationState !== EmailValidationState.VALID && (
                        <Typography color="error">
                            {t(
                                emailValidationState === EmailValidationState.INVALID_EMAIL
                                    ? 'agreements_invalid_email_message'
                                    : 'agreements_mismatched_email_message'
                            )}
                        </Typography>
                    )}

                    {isSmallScreen && <Box css={styles.nextStepWrap}>{submitButton}</Box>}
                </Box>
            </Box>
        </Box>
    );
};
